import * as React from "react";
import Layout from "../../../../../components/org/Layout";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { resultsRoute } from "../../../../../routes/org";
import { flow, pipe } from "fp-ts/function";
import {
    QueryError,
    queryStateData,
} from "../../../../../utils/supabase/QueryState";
import { either, nonEmptyArray, option, ord, task, taskEither } from "fp-ts";
import { emptyArray, errorsToMessage, runTask } from "../../../../../utils/fp";
import { reportError } from "../../../../../utils/reporting";
import { Box, Container } from "@mui/material";
import { executeSupabaseQuery } from "../../../../../utils/supabase/useQuery";
import {
    eventDetailCodec,
    eventDetailQuery,
    eventDetailQueryId,
    EventDetailRace,
} from "../../../../../hooks/event";
import { supabase } from "../../../../../utils/supabase/init";
import { EventTitle } from "../../../../../components/org/EventTitle";
import { messages } from "../../../../../utils/i18n/i18n";
import { useTranslation } from "../../../../../utils/i18n/lang";
import {
    ResultWithCategoryTitle,
    useResultsWithCategoryTitle,
} from "../../../../../hooks/results";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { concatAll } from "fp-ts/Monoid";
import {
    ordByRank,
    ordByUnfinishedReason,
    resultMessages,
    unfinishedReasonLabels,
} from "../../../../../domain/result";
import { GridActionsColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { assertKey } from "../../../../../utils/types";
import {
    RaceTabs,
    useRaceTabState,
} from "../../../../../components/org/event/RaceTabs";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { RaceId } from "../../../../../domain/event";
import { switchForenameAndSurname } from "../../../../../domain/user";
import {
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid/models/params/gridCellParams";
import {
    timeEntryFromStored,
    timeEntryToString,
} from "../../../../../domain/timing";

type SsrError =
    | { type: "query"; reason: string }
    | {
          type: "fetch";
          reason: QueryError;
      };

export const getServerSideProps = async (
    context: GetServerSidePropsContext
) => {
    return pipe(
        context.query,
        resultsRoute.queryCodec.decode,
        either.mapLeft(
            (e): SsrError => ({
                type: "query",
                reason: errorsToMessage(e),
            })
        ),
        either.map((query) => ({ query })),
        taskEither.fromEither,
        taskEither.chain(({ query }) =>
            pipe(
                executeSupabaseQuery(
                    eventDetailQueryId(query.eventId),
                    eventDetailCodec,
                    eventDetailQuery(query.eventId),
                    supabase
                ),
                taskEither.mapLeft(
                    (e): SsrError => ({
                        type: "fetch",
                        reason: e,
                    })
                ),
                taskEither.map((event) => ({
                    query,
                    event,
                }))
            )
        ),
        task.map(
            either.foldW(
                (e) => {
                    reportError("results page", e);
                    return {
                        notFound: true as const,
                    };
                },
                (props) => ({
                    props,
                })
            )
        ),
        runTask
    );
};

export default (p: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const t = useTranslation(msg);
    console.log("wholePageRender"); // TODO seems like issue whole rerender should not be needed

    return (
        <Layout
            subdomain={p.query.subdomain}
            seo={{ title: `${p.event.title} - ${t.results}`, noindex: true }}
        >
            <Container maxWidth={"lg"}>
                <EventTitle {...p.event} />
                {pipe(
                    p.event.races,
                    nonEmptyArray.fromArray,
                    option.fold(
                        () => <p>No race TODO</p>,
                        (races) => (
                            <NonEmptyRaceList
                                defaultSelectedRaceId={p.query.raceId}
                                races={races}
                            />
                        )
                    )
                )}
            </Container>
        </Layout>
    );
};

const msg = messages(
    {
        results: "results",
    },
    { cs: { results: "výsledky" } }
);

interface NonEmptyRaceListProps {
    races: NonEmptyArray<EventDetailRace>;
    defaultSelectedRaceId?: RaceId;
}
const NonEmptyRaceList = (p: NonEmptyRaceListProps) => {
    const raceTabState = useRaceTabState(
        p.races,
        resultsRoute,
        p.defaultSelectedRaceId
    );

    return (
        <>
            <RaceTabs races={p.races} state={raceTabState} />
            <Results raceId={raceTabState.selectedRaceId} />
        </>
    );
};

interface TableRow extends ResultWithCategoryTitle {}

interface TableData {
    rows: Array<TableRow>;
    columns: Array<GridColDef<TableRow> | GridActionsColDef<TableRow>>;
}

interface ResultsProps {
    raceId: RaceId;
}

const resultOrdering = concatAll(ord.getMonoid<TableRow>())([
    ordByUnfinishedReason,
    ordByRank,
]);

const Results = (p: ResultsProps) => {
    const resultsQuery = useResultsWithCategoryTitle([p.raceId]);
    const resultT = useTranslation(resultMessages);
    const unfinishedM = useTranslation(unfinishedReasonLabels);
    const tableData = React.useMemo((): TableData => {
        console.log("computing results data");

        const rows = pipe(
            resultsQuery.state,
            queryStateData,
            option.chain(nonEmptyArray.fromArray),
            option.map(flow(nonEmptyArray.sort(resultOrdering))),
            option.getOrElse(emptyArray<TableRow>())
        );

        const columns = [
            {
                flex: 0.2,
                field: assertKey<TableRow>("rank"),
                headerName: resultT.rank,
            },
            {
                flex: 1,
                field: assertKey<TableRow>("name"),
                headerName: resultT.name,
                minWidth: 160,
                valueFormatter: (
                    params: GridValueFormatterParams<TableRow["name"]>
                ) =>
                    pipe(
                        params.value,
                        option.fromNullable,
                        option.map(switchForenameAndSurname),
                        option.toNullable
                    ),
            },
            {
                flex: 0.5,
                field: assertKey<TableRow>("born"),
                headerName: resultT.born,
            },
            {
                flex: 0.5,
                field: assertKey<TableRow>("category_title"),
                headerName: resultT.category,
            },
            {
                flex: 1,
                field: assertKey<TableRow>("club"),
                headerName: resultT.club,
            },
            {
                flex: 0.6,
                field: assertKey<TableRow>("time"),
                headerName: resultT.time,
                valueGetter: (
                    params: GridValueGetterParams<TableRow["time"], TableRow>
                ) =>
                    pipe(
                        params.value,
                        option.fromNullable,
                        option.fold(
                            () =>
                                pipe(
                                    params.row.unfinished_reason,
                                    option.fromNullable,
                                    option.map((r) => unfinishedM[r]),
                                    option.getOrElse(() => "")
                                ),
                            flow(timeEntryFromStored, timeEntryToString)
                        )
                    ),
            },
            {
                field: assertKey<TableRow>("laps_completed"),
                type: "number",
                headerName: resultT.laps_completed,
            },
            {
                flex: 0.5,
                field: assertKey<TableRow>("serial_points"),
                headerName: resultT.serialPoints,
            },
        ];

        return {
            rows,
            columns,
        };
    }, [resultsQuery.state]);

    return (
        <Box sx={{ height: "60vh", display: "flex" }}>
            <Box sx={{ flexGrow: 1 }}>
                <DataGrid
                    sx={{ border: 0 }}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                        toolbar: {
                            printOptions: { disableToolbarButton: true },
                            csvOptions: {
                                delimiter: ";",
                                utf8WithBom: true,
                            },
                        },
                    }}
                    density="compact"
                    disableSelectionOnClick
                    disableDensitySelector
                    loading={resultsQuery.state.type === "loading"}
                    rows={tableData.rows}
                    columns={tableData.columns}
                />
            </Box>
        </Box>
    );
};
